import { useFetchFirstSessionExercises } from "@/infra/apis/useFetchFirstSessionExercises";
import { useIntl } from "@/infra/locale";
import { useTreatmentSelectionStore } from "@/infra/stores/treatmentSelectionStore";
import type { CopyKeyWebshop } from "@akina/infra";
import { Headline, Icon, Text } from "@akina/presentation";
import { ExerciseSlideshow } from "./ExerciseSlideshow";

type FeatureRow = {
  icon: React.ComponentProps<typeof Icon>["type"];
  titleId: CopyKeyWebshop;
  descriptionId: CopyKeyWebshop;
  iconSizeClasses: string;
};

const FEATURES: FeatureRow[] = [
  {
    icon: "phone",
    titleId: "checkoutPage.resultStep.specifiedTherapyPlan.programCard.connectWithPhysiotherapist.title",
    descriptionId: "checkoutPage.resultStep.specifiedTherapyPlan.programCard.connectWithPhysiotherapist.description",
    iconSizeClasses: "size-3",
  },
  {
    icon: "calendar",
    titleId: "checkoutPage.resultStep.specifiedTherapyPlan.programCard.structuredRecovery.title",
    descriptionId: "checkoutPage.resultStep.specifiedTherapyPlan.programCard.structuredRecovery.description",
    iconSizeClasses: "size-3",
  },
  {
    icon: "person",
    titleId: "checkoutPage.resultStep.specifiedTherapyPlan.programCard.tailoredExercises.title",
    descriptionId: "checkoutPage.resultStep.specifiedTherapyPlan.programCard.tailoredExercises.description",
    iconSizeClasses: "size-3",
  },
  {
    icon: "infinityIcon",
    titleId: "checkoutPage.resultStep.specifiedTherapyPlan.programCard.unlimitedTraining.title",
    descriptionId: "checkoutPage.resultStep.specifiedTherapyPlan.programCard.unlimitedTraining.description",
    iconSizeClasses: "size-4",
  },
];

const TAILORED_EXERCISES_TITLE_ID =
  "checkoutPage.resultStep.specifiedTherapyPlan.programCard.tailoredExercises.title" as const;

export function ProgramCard() {
  const { formatMessage } = useIntl();
  const selectedTemplateKey = useTreatmentSelectionStore(s => s.selectedTemplateKey);
  const { data } = useFetchFirstSessionExercises(selectedTemplateKey ?? undefined);
  const totalExerciseCount = data?.totalExerciseCount;

  return (
    <div className="flex h-full flex-col gap-4 rounded-2xl border-2 border-gray-800/10 bg-white p-4 md:p-6">
      <Headline size="lg" fontWeight="font-extrabold" className="text-base leading-6 text-gray-900 lg:text-lg lg:leading-6">
        {formatMessage({ id: "checkoutPage.resultStep.specifiedTherapyPlan.programCard.title" })}
      </Headline>

      <div className="flex flex-col gap-4 lg:flex-row lg:items-center">
        <div className="flex flex-1 flex-col gap-4">
          {FEATURES.map(feature => (
            <div key={feature.titleId} className="flex items-start gap-2">
              <div className="bg-primary-100 flex size-6 shrink-0 items-center justify-center rounded-full">
                <Icon type={feature.icon} sizeClasses={feature.iconSizeClasses} className="text-white" />
              </div>
              <div>
                <Text size="sm" fontWeight="font-extrabold" color="text-gray-800/70" className="lg:text-base">
                  {formatMessage(
                    { id: feature.titleId },
                    feature.titleId === TAILORED_EXERCISES_TITLE_ID && totalExerciseCount != null
                      ? { numberExercises: totalExerciseCount }
                      : undefined,
                  )}
                </Text>
                <Text size="xs" color="text-gray-800/70" className="lg:text-sm">
                  {formatMessage({ id: feature.descriptionId })}
                </Text>
              </div>
            </div>
          ))}
        </div>

        <ExerciseSlideshow templateKey={selectedTemplateKey} />
      </div>
    </div>
  );
}
